import { useCallback, useEffect, useState } from "react";
import {
  fetchAdminMedicalActs,
  type AdminMedicalAct,
} from "../api/adminApi";
import { fetchAuditorMedicalActs } from "../api/auditorApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import { loadStandardActCodes } from "../standardActCodesStorage";
import type { StandardActCode } from "../types";

function mapApiAct(row: AdminMedicalAct | Record<string, unknown>): StandardActCode {
  const r = row as AdminMedicalAct;
  return {
    id: String(r.id),
    code: String(r.code ?? ""),
    label: String(r.label || r.name || ""),
    category: String(r.category || ""),
    specialty: String(r.specialty || ""),
    description: String(r.description || ""),
  };
}

export function useAdminMedicalActs() {
  const [acts, setActs] = useState<StandardActCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setActs(loadStandardActCodes());
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const isAuditor = getStoredAccountType() === "auditor";
      const api = isAuditor ? await fetchAuditorMedicalActs() : await fetchAdminMedicalActs();
      setActs((api.medical_acts ?? []).map(mapApiAct));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les actes standards.");
      setActs(loadStandardActCodes());
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    acts,
    loading,
    error,
    usingApi,
    reload: load,
  };
}
